import type { Task } from "~skillBuilder/utils/types";

import { image, tip, title } from "../../contentBlocks";

export const TspReliability = {
  name: "Reliability",
  uniqueName: "tsp-reliability",
  icon: "Lesson",
  durationTimeText: "5 min",
  steps: [
    // Step 1
    {
      nextButtonText: "Start",
      questions: [
        // Question 1.1
        {
          attribute: "the-sub-playbook.reliability/overview",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Reliability", durationTimeText: "5 min" })}
<p>Together, we&rsquo;re on a mission to ensure no classroom is ever left without a teacher. Schools turn to you in their moments of need, relying on you to &ldquo;save the day&rdquo; when their regular teachers are away. When you commit to a school, you&rsquo;re not just picking up a shift, you&rsquo;re making a promise. While we know &ldquo;life happens,&rdquo; this guide is here to help you master your planning skills and become a legendary, ultra-reliable substitute teacher.</p>
<div align="center">
<div style="max-width:300px;width:100%;">
${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/fl_preserve_transparency/v1758202465/skillbuilderAssets/optimized/Teacher_thumbs_p.jpg", alt: "Illustration of a teacher giving a thumbs up." })}
</div>
</div>
`,
          },
        },
      ],
    },
    // Step 2
    {
      questions: [
        // Question 2.1
        {
          attribute: "the-sub-playbook.reliability/promise",
          label: "The Power of a Promise",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>The moment you click &ldquo;accept,&rdquo; a school breathes a sigh of relief. They stop worrying about coverage and start planning their day around you. Reliability means being the person they can always count on to walk through that door.</p>
`,
          },
        },
      ],
    },
    // Step 3
    {
      questions: [
        // Question 3.1
        {
          attribute: "the-sub-playbook.reliability/swing-hero",
          label: 'Level Up to "Swing Hero" Status',
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>Consistency is your ticket to the top! High reliability boosts your score and unlocks the Swing Hero title.</p>
<p><strong>The Perks of Being a Hero:</strong></p>
<ul style="margin-left: 16px;">
  <li>💰 <strong>Higher Earning Potential:</strong> More opportunities to earn.</li>
  <li>⚡ <strong>Priority Access:</strong> See the best requests first!</li>
  <li>🏅 <strong>Profile Badge:</strong> A digital &ldquo;gold star&rdquo; that schools love to see.</li>
  <li>🔄 <strong>Repeat Invites:</strong> Reliable subs get glowing feedback and personal invites back!</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 4
    {
      questions: [
        // Question 4.1
        {
          attribute: "the-sub-playbook.reliability/showing-up-intro",
          label: "Mastering the Art of Showing Up",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>It&rsquo;s simple: If you commit, show up&mdash;and show up on time. Let&rsquo;s break down how to make that happen every single time.</p>
<p><strong>❌ The &ldquo;No-Show&rdquo; No-Go</strong></p>
<p>Accepting a request and not showing up leaves a school in a massive bind&mdash;they&rsquo;re suddenly down two teachers (the regular one and you!).</p>
${tip({ text: 'Sync your Swing requests to your phone calendar and set a "leave by" alarm so you\'re never caught off guard.' })}
`,
          },
        },
      ],
    },
    // Step 5
    {
      questions: [
        // Question 5.1
        {
          attribute: "the-sub-playbook.reliability/showing-up-early",
          label: "Mastering the Art of Showing Up",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p><strong>☀️ Early is the New &ldquo;On Time&rdquo;</strong></p>
<p>The start time on your request is when the job begins.</p>
${tip({ text: "Aim to arrive early enough to overcome parking challenges, check in at the front office, and get settled in the classroom." })}
`,
          },
        },
      ],
    },
    // Step 6
    {
      questions: [
        // Question 6.1
        {
          attribute: "the-sub-playbook.reliability/showing-up-cancellation",
          label: "Mastering the Art of Showing Up",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p><strong>‼️ The 24-Hour Cancellation Rule</strong></p>
<p>Canceling with less than 24 hours&apos; notice is a major challenge for schools. It&rsquo;s hard to find a backup on such short notice.</p>
<p>⚠️ <strong>Warning:</strong> Last-minute cancellations will ding your reliability score and put your Hero status at risk. If you must cancel, always aim for at least 24 hours&apos; notice.</p>
`,
          },
        },
      ],
    },
    // Step 7
    {
      questions: [
        // Question 7.1
        {
          attribute: "the-sub-playbook.reliability/showing-up-multi-day",
          label: "Mastering the Art of Showing Up",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p><strong>🗓️ Multi-Day Commitments</strong></p>
<p>If you grab a multi-day request, you&rsquo;re committing to the entire request.</p>
${tip({ text: "Love a request but can't do one of the days? Stop! Don't accept it yet. Reach out to the Swing Support Team first to see if the school can adjust the schedule for you. If the request has already started, you can use the partial cancellation feature to release specific days without canceling the entire request. Note that the 24 hour rule still applies!" })}
`,
          },
        },
      ],
    },
    // Step 8
    {
      questions: [
        // Question 8.1
        {
          attribute: "the-sub-playbook.reliability/showing-up-finish-strong",
          label: "Mastering the Art of Showing Up",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p><strong>💪 Finish Strong (Don&rsquo;t Leave Early)</strong></p>
<p>Unless the school explicitly releases you or you&rsquo;ve pre-arranged it, stay until the scheduled end time on the request. Leaving early leaves the school scrambling for coverage at the finish line.</p>
`,
          },
        },
      ],
    },
    // Step 9
    {
      questions: [
        // Question 9.1
        {
          attribute: "the-sub-playbook.reliability/showing-up-communicate",
          label: "Mastering the Art of Showing Up",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p><strong>📣 When in Doubt, Shout it Out!</strong></p>
<p>If things go sideways, over-communicate! Late for parking? Hit traffic? Contact the school immediately. Delivering &ldquo;unfavorable news&rdquo; is always better than leaving them wondering where you are. It&rsquo;s always a good idea to keep Swing in the loop on your status.</p>
`,
          },
        },
      ],
    },
    // Step 10
    {
      questions: [
        // Question 10.1
        {
          attribute: "the-sub-playbook.reliability/curveballs",
          label: "Navigating Life’s Curveballs",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p><strong>Communication is Key!</strong> Emergencies happen. When they do, clear communication keeps everyone&mdash;you, the school, and Swing&mdash;on the same page.</p>
<ul style="margin-left: 16px;">
  <li>🤒 <strong>Feeling Sick?</strong> Cancel the moment you know you can&apos;t make it. The more notice, the better!</li>
  <li>🚗 <strong>Stuck in Traffic?</strong> Call the school with your estimated arrival time. Next time, check the school profile for parking tips and leave a little earlier.</li>
  <li>🚨 <strong>Personal Emergency at School?</strong> If you must leave a request early, tell the front office or admin immediately. Never leave students unattended&mdash;it&rsquo;s a major safety issue.</li>
</ul>
${tip({ text: "If something comes up, reach out to both the school (via the contact info on your request page) and the Swing team." })}
`,
          },
        },
      ],
    },
    // Step 11
    {
      nextButtonText: "Next Lesson",
      questions: [
        // Question 11.1
        {
          attribute: "the-sub-playbook.reliability/strategy-action",
          label: "Strategy in Action",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p><strong>Situation:</strong></p>
<p>You&rsquo;ve accepted a 3-day assignment at Newmarket Middle School starting on Tuesday. You&rsquo;re excited because you want that Profile Badge and the school has a reputation for being a great place to work.</p>
<p>The night before you set a wake-up alarm, you follow our pro-tip and sync your Swing request to your digital calendar and set a &ldquo;leave by&rdquo; alarm for 7:15 AM, giving you extra time for &ldquo;unknowns.&rdquo;</p>
<p>The morning of, you are in the car and ready to hit the road by 7:18. As soon as you hit reverse, your GPS alerts you that there has been a major accident on the route and suddenly adds 25 minutes to your arrival time, putting you 10 minutes past the school&apos;s start time.</p>
<p><strong>Potential solution:</strong></p>
<p><strong>The &ldquo;Swing Hero&rdquo; Move:</strong> You don&apos;t panic or just show up late and hope no one notices. You put the car back in park, call the Newmarket Middle School front office immediately.</p>
<p><strong>The Communication:</strong> &ldquo;Hi, this is [Your Name], the substitute for Mr. Miller. There&rsquo;s an accident along my route. I&apos;m currently ## minutes away and will be there as soon as possible.&rdquo;</p>
<p><strong>Arrival: Early is the New &ldquo;On Time&rdquo;</strong></p>
<p>Even with the traffic, because you planned for an early arrival, you walk into the front office only 2 minutes after the official start time. Because you called ahead, the office staff isn&apos;t scrambling for coverage. They were waiting for you and appreciated the heads-up.</p>
`,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
